<template>
  <v-card height="100%" flat color="bg1" class="release-detail-public overflow-y-auto">
    <!-- Page Header -->
    <v-list-item two-line :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">{{ releaseName }}</v-list-item-title>
        <v-list-item-subtitle v-if="publishedAt">
          <span class="release-detail-public__date">
            <v-icon size="14" color="primary">mdi-calendar-blank-outline</v-icon>
            {{ $formatDate(publishedAt, 'DD MMM YYYY') }}
          </span>
          <span class="release-detail-public__relative">· {{ relativeDate }}</span>
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn text small color="primary" @click="goBack">
          <v-icon left small>mdi-arrow-left</v-icon>
          Back to What's New
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>

    <div class="ds-content-width--medium pa-4">
      <!-- Loading -->
      <v-skeleton-loader v-if="isLoading" type="article" class="transparent" />

      <!-- Not found / unpublished -->
      <div v-else-if="notFound" class="ds-empty-state">
        <v-icon class="ds-empty-state__icon">mdi-file-question-outline</v-icon>
        <div class="ds-empty-state__title">Release not found</div>
        <div class="ds-empty-state__description">
          This release may have been removed, or the link is out of date.
        </div>
        <v-btn text color="primary" class="ds-empty-state__action" @click="goBack">
          Back to What's New
        </v-btn>
      </div>

      <!-- Body: the server returns already-sanitized HTML; it is rendered as-is. -->
      <div v-else-if="bodyHtml" class="release-body" v-html="bodyHtml"></div>

      <!-- Published, but the release carries no body -->
      <div v-else class="ds-empty-state">
        <v-icon class="ds-empty-state__icon">mdi-text-box-outline</v-icon>
        <div class="ds-empty-state__title">Nothing to show yet</div>
        <div class="ds-empty-state__description">This release has no notes attached.</div>
      </div>
    </div>
  </v-card>
</template>

<script>
  import dayjs from 'dayjs';
  import relativeTime from 'dayjs/plugin/relativeTime';
  import { $formatDate } from '@Utils';
  import { releaseErrorCode, isFeatureDisabledError } from '@ReleaseNotes/config/enums';
  import { error } from '@/utils/debugLogger';
  import {
    releaseBodyHtml,
    releaseName,
    releasePublishedAt,
  } from '@ReleaseNotes/utils/releaseFields';

  dayjs.extend(relativeTime);

  export default {
    name: 'ReleaseDetailPublic',
    props: {
      slug: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        notFound: false,
      };
    },
    computed: {
      release() {
        return this.$store.getters['RELEASE_NOTES/GET_TENANT_RELEASE'];
      },
      isLoading() {
        return this.$store.getters['RELEASE_NOTES/IS_TENANT_LOADING'];
      },
      releaseName() {
        return releaseName(this.release);
      },
      publishedAt() {
        return releasePublishedAt(this.release);
      },
      relativeDate() {
        return this.publishedAt ? dayjs(this.publishedAt).fromNow() : '';
      },
      bodyHtml() {
        return releaseBodyHtml(this.release);
      },
    },
    watch: {
      slug() {
        this.fetchRelease();
      },
    },
    created() {
      this.fetchRelease();
    },
    methods: {
      $formatDate,

      fetchRelease() {
        this.notFound = false;
        if (!this.slug) {
          this.notFound = true;
          return Promise.resolve();
        }

        return this.$store
          .dispatch('RELEASE_NOTES/FETCH_TENANT_RELEASE', this.slug)
          .then((data) => {
            // A 2xx with an empty body is still nothing to render.
            if (!data) this.notFound = true;
          })
          .catch((err) => {
            // A 404 / RELEASE_NOT_FOUND on a tenant link is far more likely a stale slug than
            // the management feature flag being off (this endpoint is not behind that flag), so
            // both read as "not found" — never as the feature-disabled notice.
            if (releaseErrorCode(err) === 'RELEASE_NOT_FOUND' || isFeatureDisabledError(err)) {
              this.notFound = true;
              return;
            }
            // Any other failure was already toasted by the action; the page has nothing to show.
            error('Failed to load tenant release', err);
            this.notFound = true;
          });
      },

      goBack() {
        this.$router.push({ name: 'releases' });
      },
    },
  };
</script>

<style scoped lang="scss">
  // Matches the changelog entry: the date carries the weight, the relative time supports it.
  .release-detail-public__date {
    display: inline-flex;
    align-items: center;
    gap: var(--ds-space-xs);
    font-size: var(--ds-font-body);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--v-text-base);
  }

  .release-detail-public__relative {
    opacity: 0.7;
  }

  // `.release-body` is a global class (src/features/ReleaseNotes/styles/release-body.scss,
  // imported in main.js), so it is deliberately not restyled here.
  ::v-deep .release-body {
    padding-bottom: 24px;
  }

  @media (max-width: 600px) {
    .release-detail-public ::v-deep .v-list-item__action {
      margin-top: 4px;
    }
  }
</style>
